import { useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_LEAGUE_ID, DRAFT_YEAR,
  draftRef, leagueRegistryRef,
  setDoc, getDoc,
} from '../firebase';
import { buildInitialDraftState } from '../lib/draftLogic';
import type { Manager } from '../types';

interface Props {
  onSelect: (leagueId: string) => void;
  sessionUid: string | null;
}

const EMOJI_CHOICES = [
  '🐉', '🦅', '👑', '⚡', '🐺', '🦁',
  '🐯', '🦊', '🐻', '🦈', '🐢', '🦄',
  '🐙', '🐲', '🦏', '🦬',
];

const MIN_MANAGERS = 2;
const MAX_MANAGERS = 12;
const TOTAL_NFL_TEAMS = 32;
const MIN_ROSTER_CAP = 1;
const HARD_MAX_ROSTER_CAP = 16; // arbitrary upper bound; real cap is teams/managers

function randomCode(): string {
  // 6-char alphanumeric, lowercase, easy to share
  const alphabet = 'abcdefghjkmnpqrstuvwxyz23456789';
  let out = '';
  for (let i = 0; i < 6; i++) out += alphabet[Math.floor(Math.random() * alphabet.length)];
  return out;
}

function slugify(s: string): string {
  return s.trim().toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32);
}

function managerIdFromName(name: string, fallback: string): string {
  const slug = slugify(name);
  return slug || fallback;
}

interface NewManagerDraft {
  name: string;
  emoji: string;
  budget: number;
}

function defaultManagerSlot(i: number): NewManagerDraft {
  return {
    name: '',
    emoji: EMOJI_CHOICES[i % EMOJI_CHOICES.length],
    budget: 50,
  };
}

const STARTER_MANAGERS: NewManagerDraft[] = Array.from({ length: 4 }, (_, i) => defaultManagerSlot(i));

/** Suggest a roster cap: divide the 32 teams evenly, capped at 8 (the legacy default). */
function suggestedRosterCap(managerCount: number): number {
  if (managerCount <= 0) return 8;
  return Math.max(MIN_ROSTER_CAP, Math.min(8, Math.floor(TOTAL_NFL_TEAMS / managerCount)));
}

export function LeagueSelectionScreen({ onSelect, sessionUid }: Props) {
  const [mode, setMode] = useState<'pick' | 'create' | 'join'>('pick');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create form state
  const [leagueName, setLeagueName] = useState('');
  const [managers, setManagers] = useState<NewManagerDraft[]>(STARTER_MANAGERS);
  const [commissionerIdx, setCommissionerIdx] = useState(0);
  const [rosterCap, setRosterCap] = useState<number>(suggestedRosterCap(STARTER_MANAGERS.length));
  // Track whether the user has manually overridden the suggested roster cap.
  const [rosterCapTouched, setRosterCapTouched] = useState(false);

  // Join form state
  const [joinCode, setJoinCode] = useState('');

  // Auto-suggest a roster cap when manager count changes, unless the user has overridden it.
  useEffect(() => {
    if (!rosterCapTouched) setRosterCap(suggestedRosterCap(managers.length));
  }, [managers.length, rosterCapTouched]);

  const maxRosterCapForCount = useMemo(
    () => Math.max(MIN_ROSTER_CAP, Math.min(HARD_MAX_ROSTER_CAP, Math.floor(TOTAL_NFL_TEAMS / Math.max(1, managers.length)))),
    [managers.length],
  );
  const totalRosterSlots = managers.length * rosterCap;

  const updateManager = (i: number, patch: Partial<NewManagerDraft>) => {
    setManagers(prev => prev.map((m, idx) => idx === i ? { ...m, ...patch } : m));
  };

  const addManager = () => {
    setManagers(prev => {
      if (prev.length >= MAX_MANAGERS) return prev;
      return [...prev, defaultManagerSlot(prev.length)];
    });
  };

  const removeManager = (i: number) => {
    setManagers(prev => {
      if (prev.length <= MIN_MANAGERS) return prev;
      const next = prev.filter((_, idx) => idx !== i);
      // Keep the commissioner pointer valid
      setCommissionerIdx(curr => {
        if (i === curr) return 0;
        if (i < curr) return Math.max(0, curr - 1);
        return curr;
      });
      return next;
    });
  };

  const handleCreate = async () => {
    setError(null);
    const trimmedName = leagueName.trim();
    if (!trimmedName) { setError('League name is required.'); return; }
    if (managers.length < MIN_MANAGERS || managers.length > MAX_MANAGERS) {
      setError(`Leagues need ${MIN_MANAGERS}–${MAX_MANAGERS} managers.`);
      return;
    }
    if (!Number.isFinite(rosterCap) || rosterCap < MIN_ROSTER_CAP) {
      setError('Roster cap must be at least 1.');
      return;
    }
    if (managers.length * rosterCap > TOTAL_NFL_TEAMS) {
      setError(`Total roster slots (${managers.length} × ${rosterCap} = ${managers.length * rosterCap}) exceeds the 32 NFL teams.`);
      return;
    }
    const filled = managers.map((m, i) => ({ ...m, name: m.name.trim() || `Manager ${i + 1}` }));
    const ids = filled.map((m, i) => managerIdFromName(m.name, `mgr${i + 1}`));
    if (new Set(ids).size !== ids.length) {
      setError('Manager names must be unique.');
      return;
    }
    for (const m of filled) {
      if (!Number.isFinite(m.budget) || m.budget < rosterCap) {
        setError(`Each budget must be at least the roster cap ($${rosterCap}) so every slot can be filled.`);
        return;
      }
    }

    setBusy(true);
    try {
      // Find a free league code.
      let code = slugify(trimmedName) || randomCode();
      if (code === DEFAULT_LEAGUE_ID) code = randomCode();
      for (let attempt = 0; attempt < 5; attempt++) {
        const existing = await getDoc(draftRef(code));
        if (!existing.exists()) break;
        code = randomCode();
      }

      const newManagers: Manager[] = filled.map((m, i) => ({
        id: ids[i],
        name: m.name,
        emoji: m.emoji,
        budget: m.budget,
        initialBudget: m.budget,
        teams: [],
        teamsDrafted: 0,
      }));

      const initial = buildInitialDraftState(DRAFT_YEAR, {
        managers: newManagers,
        leagueId: code,
        leagueName: trimmedName,
        commissionerIds: [ids[commissionerIdx]],
        rosterCap,
      });

      // Write league state + registry entry.
      await setDoc(draftRef(code), initial);
      await setDoc(leagueRegistryRef(code), {
        leagueId: code,
        name: trimmedName,
        createdAt: Date.now(),
        year: DRAFT_YEAR,
        managerCount: newManagers.length,
        rosterCap,
      });

      onSelect(code);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to create league.');
    } finally {
      setBusy(false);
    }
  };

  const handleJoin = async () => {
    setError(null);
    const code = joinCode.trim().toLowerCase();
    if (!code) { setError('Enter a league code.'); return; }
    setBusy(true);
    try {
      const snap = await getDoc(draftRef(code));
      if (!snap.exists()) { setError(`No league found for code "${code}".`); return; }
      onSelect(code);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to join league.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-950 via-brand-bg to-slate-900 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="panel p-8 max-w-2xl w-full">
        <h1 className="text-4xl font-extrabold bg-gradient-to-r from-teal-300 to-cyan-400 bg-clip-text text-transparent text-center mb-2">
          🏈 NFL Fantasy Auction
        </h1>
        <p className="text-slate-400 text-center mb-6">Pick a league to continue.</p>

        {mode === 'pick' && (
          <div className="space-y-3">
            <button
              onClick={() => onSelect(DEFAULT_LEAGUE_ID)}
              className="w-full p-5 rounded-xl bg-slate-800 hover:bg-teal-600 transition border border-slate-700 hover:border-teal-400 text-left"
            >
              <div className="text-lg font-bold text-white">Continue to the original league</div>
              <div className="text-sm text-slate-300">The 4-manager league you've been using.</div>
            </button>
            <button
              onClick={() => { setMode('create'); setError(null); }}
              className="w-full p-5 rounded-xl bg-slate-800 hover:bg-teal-600 transition border border-slate-700 hover:border-teal-400 text-left"
            >
              <div className="text-lg font-bold text-white">➕ Create a new league</div>
              <div className="text-sm text-slate-300">Pick a name, add 2–12 managers, get a shareable code.</div>
            </button>
            <button
              onClick={() => { setMode('join'); setError(null); }}
              className="w-full p-5 rounded-xl bg-slate-800 hover:bg-teal-600 transition border border-slate-700 hover:border-teal-400 text-left"
            >
              <div className="text-lg font-bold text-white">🔗 Join an existing league</div>
              <div className="text-sm text-slate-300">Enter the league code your commissioner shared.</div>
            </button>
          </div>
        )}

        {mode === 'create' && (
          <div className="space-y-4">
            <label className="block">
              <span className="text-xs uppercase tracking-wide text-slate-400">League name</span>
              <input
                type="text"
                value={leagueName}
                onChange={e => setLeagueName(e.target.value)}
                placeholder="e.g. Friday Night Auction"
                className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white"
                maxLength={40}
              />
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-slate-400">
                  Roster cap (teams per manager)
                </span>
                <input
                  type="number"
                  value={rosterCap}
                  min={MIN_ROSTER_CAP}
                  max={maxRosterCapForCount}
                  onChange={e => {
                    setRosterCap(Number(e.target.value) || 0);
                    setRosterCapTouched(true);
                  }}
                  className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Max {maxRosterCapForCount} for {managers.length} managers ({managers.length}×{maxRosterCapForCount}={managers.length * maxRosterCapForCount} ≤ 32).
                </p>
              </label>
              <div className="block">
                <span className="text-xs uppercase tracking-wide text-slate-400">Total roster slots</span>
                <div className={`mt-1 px-3 py-2 rounded-lg border ${totalRosterSlots > TOTAL_NFL_TEAMS ? 'border-rose-500 text-rose-300' : 'border-slate-700 text-slate-200'} bg-slate-900`}>
                  {managers.length} × {rosterCap} = <span className="font-bold">{totalRosterSlots}</span>
                  <span className="text-slate-500 text-sm"> / 32</span>
                </div>
                {totalRosterSlots > TOTAL_NFL_TEAMS && (
                  <p className="text-[11px] text-rose-400 mt-1">Too many slots — not enough NFL teams.</p>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-wide text-slate-400">
                  Managers ({managers.length})
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => removeManager(managers.length - 1)}
                    disabled={managers.length <= MIN_MANAGERS}
                    className="btn-ghost text-xs px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    − Remove
                  </button>
                  <button
                    type="button"
                    onClick={addManager}
                    disabled={managers.length >= MAX_MANAGERS}
                    className="btn-ghost text-xs px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    + Add manager
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                {managers.map((m, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <select
                      value={m.emoji}
                      onChange={e => updateManager(i, { emoji: e.target.value })}
                      className="bg-slate-900 border border-slate-700 rounded-lg px-2 py-2 text-lg"
                    >
                      {EMOJI_CHOICES.map(em => <option key={em} value={em}>{em}</option>)}
                    </select>
                    <input
                      type="text"
                      value={m.name}
                      onChange={e => updateManager(i, { name: e.target.value })}
                      placeholder={`Manager ${i + 1}`}
                      className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white"
                      maxLength={24}
                    />
                    <div className="flex items-center gap-1">
                      <span className="text-slate-400 text-sm">$</span>
                      <input
                        type="number"
                        value={m.budget}
                        min={1}
                        onChange={e => updateManager(i, { budget: Number(e.target.value) || 0 })}
                        className="w-20 bg-slate-900 border border-slate-700 rounded-lg px-2 py-2 text-white text-right"
                      />
                    </div>
                    <label className="flex items-center gap-1 text-xs text-slate-300 cursor-pointer" title="Commissioner">
                      <input
                        type="radio"
                        name="commish"
                        checked={commissionerIdx === i}
                        onChange={() => setCommissionerIdx(i)}
                      />
                      🛡
                    </label>
                    <button
                      type="button"
                      onClick={() => removeManager(i)}
                      disabled={managers.length <= MIN_MANAGERS}
                      className="text-slate-500 hover:text-rose-400 disabled:opacity-30 disabled:cursor-not-allowed text-lg leading-none px-1"
                      title="Remove this manager"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 mt-2">🛡 marks the commissioner. They can veto, undo, archive, and execute trades.</p>
            </div>

            {error && <div className="text-rose-400 text-sm">{error}</div>}

            <div className="flex gap-2 justify-end pt-2">
              <button onClick={() => setMode('pick')} className="btn-ghost" disabled={busy}>Back</button>
              <button onClick={handleCreate} className="btn-primary" disabled={busy}>
                {busy ? 'Creating…' : 'Create league'}
              </button>
            </div>
          </div>
        )}

        {mode === 'join' && (
          <div className="space-y-4">
            <label className="block">
              <span className="text-xs uppercase tracking-wide text-slate-400">League code</span>
              <input
                type="text"
                value={joinCode}
                onChange={e => setJoinCode(e.target.value)}
                placeholder="e.g. friday-night-auction"
                className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white font-mono"
                autoFocus
              />
            </label>
            {error && <div className="text-rose-400 text-sm">{error}</div>}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setMode('pick')} className="btn-ghost" disabled={busy}>Back</button>
              <button onClick={handleJoin} className="btn-primary" disabled={busy}>
                {busy ? 'Checking…' : 'Join league'}
              </button>
            </div>
          </div>
        )}

        <p className="text-xs text-slate-600 mt-6 text-center">Session: {sessionUid?.slice(0, 8) ?? 'connecting…'}</p>
      </div>
    </div>
  );
}
